import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../../api';
import { DialogLayer } from '../../ui/Dialog';

type Mode = 'off' | 'test' | 'demo' | 'prod';

/** `GET /api/ksef/settings` (owner only): never the token — only whether one is saved and when. */
export type KsefSettings = { saved: boolean; env: Mode; nip: string; tokenSet: boolean; tokenSavedAt: string | null };
type CheckResult = { ok: boolean; message: string };

const SETTINGS_KEY = ['ksef', 'settings'] as const;
const ENVS: Array<[Mode, string]> = [
  ['off', 'Wyłączone'],
  ['test', 'Testowe'],
  ['demo', 'Przedprodukcyjne (DEMO)'],
  ['prod', 'Produkcyjne'],
];
const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const digits = (nip: string) => nip.replace(/[\s-]/g, '');
const errorText = (e: unknown, fallback: string) => {
  if (!(e instanceof ApiError)) return fallback;
  const issues = fieldIssues(e);
  return issues.length > 0 ? issues.map((i) => i.message).join(' · ') : e.message;
};

/**
 * "Finanse → Ustawienia → KSeF", the owner's part (2026-11-01): environment, NIP and the token. The token field is
 * write-only — after saving only „Token zapisany <data>” with „Zmień token” and „Usuń”. Switching to production asks
 * once („Przełącz na produkcję”); `currentMode` is the mode in force (from the status card).
 */
export function KsefSettingsForm({ currentMode }: { currentMode: Mode }) {
  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api<KsefSettings>('/api/ksef/settings') });
  if (settings.error) {
    return (
      <p className="alert" role="alert">
        {errorText(settings.error, 'Nie udało się wczytać ustawień KSeF')}
      </p>
    );
  }
  if (!settings.data) return null;
  return <Form initial={settings.data} currentMode={currentMode} />;
}

function Form({ initial, currentMode }: { initial: KsefSettings; currentMode: Mode }) {
  const queryClient = useQueryClient();
  const [env, setEnv] = useState<Mode>(initial.env);
  const [nip, setNip] = useState(initial.nip);
  const [token, setToken] = useState('');
  const [changing, setChanging] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [saved, setSaved] = useState(false);
  // the query's data (kept fresh by `done`): the field states follow what the server has
  const current = initial;

  const done = (view: KsefSettings) => {
    queryClient.setQueryData(SETTINGS_KEY, view);
    void queryClient.invalidateQueries({ queryKey: ['ksef', 'status'] });
    setToken('');
    setChanging(false);
  };
  const save = useMutation({
    mutationFn: (confirmProd: boolean) =>
      api<KsefSettings>('/api/ksef/settings', { method: 'PUT', body: { env, nip: digits(nip), ...(token.trim() ? { token: token.trim() } : {}), ...(confirmProd ? { confirmProd: true } : {}) } }),
    onSuccess: (view) => {
      done(view);
      setConfirm(false);
      setSaved(true);
    },
    onError: () => setConfirm(false),
  });
  const remove = useMutation({
    mutationFn: () => api<KsefSettings>('/api/ksef/settings/token', { method: 'DELETE' }),
    onSuccess: done,
  });
  const check = useMutation({
    mutationFn: () => api<CheckResult>('/api/ksef/settings/check', { method: 'POST', body: { env, nip: digits(nip), ...(token.trim() ? { token: token.trim() } : {}) } }),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    check.reset();
    if (env === 'prod' && currentMode !== 'prod') setConfirm(true);
    else save.mutate(false);
  }
  const showTokenField = !current.tokenSet || changing;
  const busy = save.isPending || remove.isPending;
  const failure = save.error ?? remove.error;

  return (
    <form className="ksef-settings" onSubmit={submit} data-feature-id="ksef.settings" aria-label="Ustawienia KSeF">
      <h3>Ustawienia połączenia</h3>
      <label className="field">
        <span>Środowisko</span>
        <select value={env} onChange={(e) => setEnv(e.target.value as Mode)}>
          {ENVS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>NIP firmy</span>
        <input value={nip} onChange={(e) => setNip(e.target.value)} inputMode="numeric" autoComplete="off" />
        {/* review M2: the download position belongs to the NIP — the server starts it anew */}
        {env !== 'off' && env === current.env && current.nip !== '' && digits(nip) !== current.nip && <span className="muted">Po zapisaniu nowego NIP-u pobieranie faktur z KSeF zacznie się od nowa.</span>}
      </label>
      {showTokenField ? (
        <div className="ksef-token">
          <label className="field">
            <span>Token KSeF</span>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="new-password" spellCheck={false} placeholder="Wklej token z Aplikacji Podatnika KSeF" />
          </label>
          {current.tokenSet && (
            <button
              className="link-button"
              type="button"
              onClick={() => {
                setToken('');
                setChanging(false);
              }}
            >
              Anuluj
            </button>
          )}
        </div>
      ) : (
        <p className="ksef-token">
          <span>{`Token zapisany ${current.tokenSavedAt ? WHEN.format(new Date(current.tokenSavedAt)) : ''}`.trim()}</span>{' '}
          <button className="link-button" type="button" onClick={() => setChanging(true)}>
            Zmień token
          </button>{' '}
          <button className="link-button" type="button" disabled={busy} onClick={() => remove.mutate()}>
            Usuń
          </button>
        </p>
      )}
      {failure && (
        <p className="alert" role="alert">
          {errorText(failure, 'Nie udało się zapisać ustawień KSeF')}
        </p>
      )}
      {check.error && (
        <p className="alert" role="alert">
          {errorText(check.error, 'Nie udało się sprawdzić połączenia')}
        </p>
      )}
      {check.data &&
        (check.data.ok ? (
          <p role="status">{check.data.message}</p>
        ) : (
          <p className="alert" role="alert">
            {check.data.message}
          </p>
        ))}
      {saved && !save.isPending && <p role="status">Zapisano.</p>}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={busy}>
          Zapisz
        </button>
        <button className="btn" type="button" disabled={check.isPending || env === 'off'} onClick={() => check.mutate()}>
          {check.isPending ? 'Sprawdzanie…' : 'Sprawdź połączenie'}
        </button>
      </div>
      {confirm && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="ksef-prod-title">
            <DialogLayer onClose={() => setConfirm(false)} busy={save.isPending} />
            <h2 id="ksef-prod-title">Przełączyć KSeF na produkcję?</h2>
            <p>Od tej chwili faktury wysyłane do KSeF mają moc prawną.</p>
            <div className="actions">
              <button className="btn primary" type="button" disabled={save.isPending} onClick={() => save.mutate(true)}>
                Przełącz na produkcję
              </button>
              <button className="btn" type="button" onClick={() => setConfirm(false)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
